import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Bell, BellRing, X } from 'lucide-react'
import { api } from '@/lib/api'
import { formatDateTime } from '@/lib/date'
import { cn } from '@/lib/cn'
import { disablePush, enablePush, getPushStatus, type PushStatus } from '@/lib/push'
import { useToast } from '@/components/ui/toast'
import { useAuth } from '@/context/AuthContext'
import { BottomTabContent, bottomTabClass } from '@/components/ui/BottomTab'
import type { AppNotification } from '@/types'

/**
 * The header bell. Polls /notifications every 30s; opening the panel marks
 * everything read. Sits on the blue top bar, so the trigger styling matches
 * the other header icons. The panel footer manages phone push for this
 * device - the enable action must stay behind the button click (iOS only
 * allows the permission prompt inside a user gesture).
 *
 * `variant="tab"` is the same bell as a slot in PortalBottomNav: the trigger
 * is a tab and the panel rises from the bar, stopping at its top edge so the
 * bar stays visible and usable.
 */
export default function NotificationBell({ variant = 'header' }: { variant?: 'header' | 'tab' }) {
  const [open, setOpen] = useState(false)
  const [pushStatus, setPushStatus] = useState<PushStatus>('unsupported')
  const [pushBusy, setPushBusy] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const toast = useToast()
  const { impersonating } = useAuth()
  const { pathname } = useLocation()
  const reduce = useReducedMotion()

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () =>
      (await api.get<{ data: AppNotification[]; unread_count: number }>('/notifications')).data,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  })

  const markRead = useMutation({
    mutationFn: async () => (await api.post('/notifications/read')).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })

  useEffect(() => {
    // The tab panel has its own Escape and scrim below. It is also portalled,
    // so a tap inside it would count as "outside" here and close it.
    if (!open || variant !== 'header') return
    const onMouseDown = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, variant])

  // Tab panel: Escape, scroll lock, and closing if the screen widens past xl,
  // where the bar and the panel hide and would leave the lock on unseen.
  useEffect(() => {
    if (!open || variant !== 'tab') return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    const wide = window.matchMedia('(min-width: 1280px)')
    const closeIfWide = () => {
      if (wide.matches) setOpen(false)
    }
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKeyDown)
    wide.addEventListener('change', closeIfWide)
    return () => {
      document.body.style.overflow = previous
      document.removeEventListener('keydown', onKeyDown)
      wide.removeEventListener('change', closeIfWide)
    }
  }, [open, variant])

  // Tapping another tab (or any navigation) closes the panel.
  useEffect(() => {
    setOpen(false)
  }, [pathname])

  // Known before the first open too: resolved only on open, the footer
  // appeared mid-animation and the rising panel jumped taller.
  useEffect(() => {
    getPushStatus().then(setPushStatus)
  }, [])

  useEffect(() => {
    if (!open) return
    getPushStatus().then(setPushStatus)
  }, [open])

  const unread = data?.unread_count ?? 0

  const toggle = () => {
    const next = !open
    setOpen(next)
    if (next && unread > 0) markRead.mutate()
  }

  const close = useCallback(() => setOpen(false), [])

  const onEnablePush = async () => {
    setPushBusy(true)
    try {
      await enablePush()
      toast.success('Push notifications on', 'This device will now get alerts even when the app is closed.')
    } catch (err) {
      toast.error('Could not enable push', err instanceof Error ? err.message : undefined)
    } finally {
      setPushBusy(false)
      setPushStatus(await getPushStatus())
    }
  }

  const onDisablePush = async () => {
    setPushBusy(true)
    try {
      await disablePush()
      toast.info('Push notifications off for this device.')
    } catch {
      toast.error('Could not turn off push notifications.')
    } finally {
      setPushBusy(false)
      setPushStatus(await getPushStatus())
    }
  }

  const openItem = (item: AppNotification) => {
    setOpen(false)
    if (item.data.url) navigate(item.data.url)
  }

  const items = (
    <>
      {(data?.data.length ?? 0) === 0 && (
        <p className="px-3 py-6 text-center text-sm text-muted">Nothing yet.</p>
      )}
      {data?.data.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => openItem(item)}
          className={cn(
            'block w-full rounded-lg px-3 py-2.5 text-left hover:bg-foreground/5',
            !item.read_at && 'bg-primary/5',
          )}
        >
          <p className="text-sm font-semibold">{item.data.title}</p>
          <p className="mt-0.5 text-xs text-foreground/70">{item.data.body}</p>
          <p className="mt-1 text-xs text-muted">{formatDateTime(item.created_at)}</p>
        </button>
      ))}
    </>
  )

  // No push controls while an admin is signed in as someone else: this phone
  // is the admin's device, not the member's.
  const pushControls =
    pushStatus !== 'unsupported' && !impersonating ? (
      <>
        {pushStatus === 'available' && (
          <button
            type="button"
            onClick={onEnablePush}
            disabled={pushBusy}
            className="flex w-full items-center gap-2 rounded-lg border border-primary/60 bg-primary/20 px-3 py-2 text-sm font-medium text-primary hover:bg-primary/30 disabled:opacity-60"
          >
            <BellRing className="size-4 stroke-[1.5]" />
            {pushBusy ? 'Enabling...' : 'Enable push notifications'}
          </button>
        )}
        {pushStatus === 'subscribed' && (
          <p className="flex items-center justify-between text-xs text-muted">
            <span className="flex items-center gap-1.5">
              <BellRing className="size-3.5 text-success" />
              Push is on for this device
            </span>
            <button
              type="button"
              onClick={onDisablePush}
              disabled={pushBusy}
              className="font-medium text-foreground/70 underline hover:text-foreground disabled:opacity-60"
            >
              Turn off
            </button>
          </p>
        )}
        {pushStatus === 'need-install' && (
          <p className="text-xs text-muted">
            To get notifications on iPhone, add MPG to your Home Screen first: open the
            Share menu, then Add to Home Screen, and open the app from that icon.
          </p>
        )}
        {pushStatus === 'denied' && (
          <p className="text-xs text-muted">
            Notifications are blocked for this site in your browser settings.
          </p>
        )}
      </>
    ) : null

  if (variant === 'tab') {
    return (
      <>
        <button
          type="button"
          onClick={toggle}
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
          aria-expanded={open}
          className={bottomTabClass(open)}
        >
          {/* "Notifications" truncates in a 72px slot on a 360px phone; the panel title keeps the full word. */}
          <BottomTabContent icon={<Bell className="stroke-[1.5]" />} label="Alerts" badge={unread} />
        </button>
        {createPortal(
          <AnimatePresence>
            {open && (
              // Stops at the bar's top edge (56px bar, its 1px hairline and the
              // home indicator), so the bar stays above the scrim and Alerts
              // closes the panel again. The scrim fades and the sheet slides up
              // from behind the bar. Both animate opacity or a `transform`
              // string, which framer-motion hands to the compositor; `y` runs
              // on the main thread and stuttered on Android phones.
              <motion.div className="fixed inset-x-0 bottom-[calc(3.5rem+1px+env(safe-area-inset-bottom))] top-0 z-30 flex flex-col justify-end xl:hidden">
                <motion.div
                  className="absolute inset-0 bg-foreground/40"
                  onMouseDown={close}
                  initial={reduce ? false : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={reduce ? undefined : { opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                />
                <motion.div
                  role="dialog"
                  aria-label="Notifications"
                  className="relative mx-auto flex max-h-[70vh] w-full max-w-lg flex-col rounded-t-2xl border border-b-0 border-foreground/10 bg-background text-left text-foreground shadow-xl"
                  initial={reduce ? false : { transform: 'translateY(100%)' }}
                  animate={{ transform: 'translateY(0%)' }}
                  exit={reduce ? undefined : { transform: 'translateY(100%)', transition: { duration: 0.2, ease: [0.4, 0, 1, 1] } }}
                  transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
                >
                  <div className="flex items-center justify-between py-1.5 pl-5 pr-2">
                    <h3 className="text-base font-medium">Notifications</h3>
                    <button
                      type="button"
                      onClick={close}
                      aria-label="Close notifications"
                      className="flex size-10 items-center justify-center rounded-lg text-muted hover:bg-foreground/5 hover:text-ink"
                    >
                      <X className="size-5" />
                    </button>
                  </div>
                  {/* px-2 plus the items' own px-3 lines their text up with the title. */}
                  <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">{items}</div>
                  {pushControls && <div className="border-t border-foreground/10 px-5 py-3">{pushControls}</div>}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
      </>
    )
  }

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={toggle}
        aria-label="Notifications"
        aria-expanded={open}
        className="relative flex size-10 items-center justify-center rounded-full text-background/80 hover:bg-background/10 hover:text-background"
      >
        <Bell className="size-4 stroke-[1.5]" />
        {unread > 0 && (
          <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-background">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {/* .box forces position:relative (see index.css gotcha) - keep absolute on the wrapper, not the .box */}
      {open && (
        // Width capped to the viewport: the bell sits ~60px from the right
        // edge, so a full 320px panel would clear the left edge on phones.
        <div className="absolute right-0 top-full z-50 mt-2 w-80 max-w-[calc(100vw-4rem)]">
          <div className="box p-2 text-left text-foreground">
            <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-muted">
              Notifications
            </p>
            {/* The list scrolls, not the .box - its ::before frame pokes 5px
                past the bottom and would otherwise register as overflow,
                showing a scrollbar even on an empty panel. */}
            <div className="max-h-80 overflow-y-auto">{items}</div>
            {pushControls && <div className="mt-1 border-t border-foreground/10 px-3 py-2.5">{pushControls}</div>}
          </div>
        </div>
      )}
    </div>
  )
}
